import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PrimaryButton, InputField, TextLink } from "@/components/fionova/Primitives";
import { useAuth } from "@/context/AuthContext";
import { claimGuestJourney } from "@/lib/journey";
import { LegalNotice } from "@/components/fionova/LegalNotice";
import { MarketingOptIn } from "@/components/fionova/MarketingOptIn";

// One account, one password: create a free account OR sign in to an existing one. The guest journey moves into it.
// No native <form> so tag auto form-capture never reads field values.
export const AccountLink = ({ onDone, note, initialMode = "create", email = "", lockEmail = false, footnote = "Your journey, reflection and Companion Memory move into this one account." }) => {
  const navigate = useNavigate();
  const { register, login, refresh } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [f, setF] = useState({ name: "", email, password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [optIn, setOptIn] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const ok = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim()) && f.password.length >= 6 && !busy;

  const submit = async () => {
    if (!ok) return;
    setBusy(true); setError("");
    try {
      if (mode === "create") await register({ email: f.email.trim(), password: f.password, name: f.name.trim(), marketingOptIn: optIn });
      else await login({ email: f.email.trim(), password: f.password });
      await claimGuestJourney();
      const u = await refresh();
      await onDone(u);
    } catch (e) {
      if (mode === "create" && e.status === 409) {
        setMode("signin"); setF((x) => ({ ...x, password: "" }));
        setError("This email already has a FioNova account. Sign in with your existing password. No second account is needed.");
      } else setError(e.message || "That didn't work. Please try again.");
    }
    setBusy(false);
  };
  const onKey = (e) => { if (e.key === "Enter") submit(); };

  return (
    <div className="space-y-3" data-testid="account-link" data-mode={mode}>
      <div className="flex rounded-full bg-slate-900/5 p-1 text-sm font-medium" role="tablist">
        {[["create", "Create my free account"], ["signin", "I already have an account"]].map(([k, label]) => (
          <button key={k} role="tab" aria-selected={mode === k} data-testid={`account-link-${k}`} onClick={() => { setMode(k); setError(""); }}
            className={`flex-1 rounded-full px-3 py-2 transition-colors ${mode === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>{label}</button>
        ))}
      </div>
      {note && <p className="text-[13px] leading-relaxed text-slate-600" data-testid="account-link-note">{note}</p>}
      {mode === "create" && <InputField testId="account-link-name" label="Your first name (optional)" value={f.name} onChange={set("name")} onKeyDown={onKey} autoComplete="given-name" />}
      <InputField testId="account-link-email" label={lockEmail && mode === "create" ? "Email (from your checkout receipt)" : "Email"} type="email" value={f.email} onChange={set("email")} onKeyDown={onKey} autoComplete="email" readOnly={lockEmail && mode === "create"} />
      <InputField testId="account-link-password" label={mode === "create" ? "Choose a password (6+ characters)" : "Your password"} type="password"
        value={f.password} onChange={set("password")} onKeyDown={onKey} autoComplete={mode === "create" ? "new-password" : "current-password"} />
      {error && <p className="text-sm text-rose-600" data-testid="account-link-error">{error}</p>}
      <PrimaryButton data-testid="account-link-submit" onClick={submit} disabled={!ok}>
        {busy ? "One moment…" : mode === "create" ? "Create account and continue" : "Sign in and continue"}
      </PrimaryButton>
      {mode === "signin" && (
        <div className="text-center"><TextLink data-testid="account-link-forgot" onClick={() => navigate("/forgot-password")} className="text-sm">Forgot your password?</TextLink></div>
      )}
      <p className="text-center text-xs text-slate-500">{footnote}</p>
      {mode === "create" && <MarketingOptIn checked={optIn} onChange={setOptIn} testId="account-link-marketing" />}
      <LegalNotice action={mode === "create" ? "creating an account" : "signing in"} testId="account-link-legal" />
    </div>
  );
};
